import {
  HasuraDataTableColumnDef,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
  ConfigurationContext,
  NotificationsContext,
} from '@kirz/mui-admin';
import { Close } from '@mui/icons-material';
import {
  Button,
  DialogContent,
  Dialog,
  DialogTitle,
  IconButton,
  Checkbox,
} from '@mui/material';
import React, {
  forwardRef,
  Ref,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import Overview from 'pages/forms/personenbelasting-aangifte-voorbereiding-2025/Overview';

type ProcessedCheckboxProps = {
  id: number;
  value: boolean;
  onChange: (value: boolean) => void;
};

function ProcessedCheckbox({ id, value, onChange }: ProcessedCheckboxProps) {
  const [isChecked, setIsChecked] = useState<boolean>(value);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { hasura } = useContext(ConfigurationContext);

  const saveState = async (value: boolean) => {
    try {
      setIsLoading(true);

      await Promise.all([
        hasura.request({
          type: 'mutation',
          source: 'clientWebForm',
          where: {
            id: { _eq: id },
          },
          action: 'update',
          set: {
            isProcessed: value,
          },
        }),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Checkbox
        checked={isChecked}
        onChange={(e) => {
          setIsChecked(e.target.checked);
          saveState(e.target.checked);
        }}
        disabled={isLoading}
      />
    </>
  );
}

export const WebFormUsersTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
      webFormId: string;
      filterColumns?: (
        columns: HasuraDataTableColumnDef[],
      ) => HasuraDataTableColumnDef[];
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const { hasura } = useContext(ConfigurationContext);
    const { showPrompt, showAlert } = useContext(NotificationsContext);

    const { filterColumns, webFormId, ...rest } = props;
    const [openedForm, setOpenedForm] = useState<any>(null);

    const tableRef = useRef<DataTableExRef>(null);

    useImperativeHandle(ref, () => tableRef.current!, []);

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () =>
        (filterColumns || ((x: HasuraDataTableColumnDef[]) => x))([
          {
            field: 'firstName',
            headerName: 'Voornaam',
          },
          {
            field: 'lastName',
            headerName: 'Achternaam',
          },
          {
            field: 'formInvitation',
            headerName: 'Uitnodiging',
            selector: false,
            sortable: false,
            type: 'boolean',
            valueGetter: (params) => {
              return params.row.isInvited;
            },
          },
          {
            field: 'formReminder1',
            headerName: 'Herinnering 1',
            selector: false,
            sortable: false,
            type: 'boolean',
            valueGetter: (params) => {
              return params.row.isReminder1Sent;
            },
          },
          {
            field: 'formReminder2',
            headerName: 'Herinnering 2',
            selector: false,
            sortable: false,
            type: 'boolean',
            valueGetter: (params) => {
              return params.row.isReminder2Sent;
            },
          },
          {
            field: 'formReminder3',
            headerName: 'Herinnering 3',
            selector: false,
            sortable: false,
            type: 'boolean',
            valueGetter: (params) => {
              return params.row.isReminder3Sent;
            },
          },
          {
            field: 'processed',
            headerName: 'Verwerkt',
            flex: 1,
            selector: false,
            renderCell(params) {
              return (
                <ProcessedCheckbox
                  id={params.row.clientFormId}
                  value={params.row.isProcessed}
                  onChange={() => {}}
                />
              );
            },
          },
          {
            field: 'filledForm',
            headerName: 'Ingevuld formulier',
            selector: false,
            sortable: false,
            renderCell: (params) => {
              return (
                <Button
                  disabled={!params.row.isFilled}
                  href={`/forms/${params.row.formSlug
                    .toLowerCase()
                    .replaceAll(' ', '-')}?clientId=${params.row.id}`}
                  target="_blank"
                >
                  Open
                </Button>
              );
            },
          },
          // {
          //   field: 'resetForm',
          //   headerName: ' ',
          //   selector: false,
          //   sortable: false,
          //   renderCell: (params) => {
          //     return (
          //       <Button
          //         color="error"
          //         onClick={async () => {
          //           const confirmed = await showPrompt({
          //             title: 'Weet u zeker dat u het formulier wilt heropenen?',
          //             accept: 'Heropenen',
          //             cancel: 'Annuleren',
          //           });

          //           if (!confirmed) {
          //             return;
          //           }

          //           await hasura.request({
          //             type: 'mutation',
          //             source: 'clientWebForm',
          //             where: {
          //               id: { _eq: params.row.clientFormId },
          //             },
          //             action: 'update',
          //             set: {
          //               isFilled: false,
          //               filledAt: null,
          //               data: {
          //                 ...params.row.data,
          //                 step: 1,
          //               },
          //             },
          //           });

          //           showAlert('Formulier heropend', 'success');

          //           tableRef.current?.reload();
          //         }}
          //       >
          //         Heropenen
          //       </Button>
          //     );
          //   },
          // },
        ]),
      [filterColumns],
    );

    return (
      <>
        <DataTableEx
          {...rest}
          ref={tableRef}
          source="client"
          columns={columns}
          persistStateMode="query"
          sortBy={rest.sortBy ?? { field: 'firstName', sort: 'asc' }}
          editable={false}
          deletable={false}
          selectProps={{
            ...rest.selectProps,
            onSelection(selections) {
              return [
                ...selections,
                `forms(where: {webFormId: {_eq: ${webFormId}}}) {
                  id
                  form {
                    slug
                    name
                    deadline
                    reminder1Date
                    reminder2Date
                    reminder3Date
                  }
                  isFilled
                  isInvited
                  isReminder1Sent
                  isReminder2Sent
                  isReminder3Sent
                  filledAt
                  data
                  isProcessed
                }`,
              ];
            },
            onFetch(items) {
              return items.map((item) => {
                const form = item.forms[0]?.form;
                return {
                  ...item,
                  formSlug: form?.slug ?? null,
                  formName: form?.name ?? null,
                  formDeadline: form?.deadline ?? null,
                  formReminder1: form?.reminder1Date ?? null,
                  formReminder2: form?.reminder2Date ?? null,
                  formReminder3: form?.reminder3Date ?? null,
                  isFilled: item.forms[0]?.isFilled ?? false,
                  isInvited: item.forms[0]?.isInvited ?? false,
                  filledAt: item.forms[0]?.filledAt ?? null,
                  isReminder1Sent: item.forms[0]?.isReminder1Sent ?? false,
                  isReminder2Sent: item.forms[0]?.isReminder2Sent ?? false,
                  isReminder3Sent: item.forms[0]?.isReminder3Sent ?? false,
                  data: item.forms[0]?.data ?? null,
                  isProcessed: item.forms[0]?.isProcessed ?? false,
                  clientFormId: item.forms[0]?.id ?? null,
                };
              });
            },
            filter: {
              ...rest.selectProps?.filter,
              forms: {
                webFormId: { _eq: webFormId },
              },
            },
          }}
          formDialogProps={{
            ...rest.formDialogProps,
            formProps: {
              ...rest.formDialogProps?.formProps,
              defaultValues: {
                ...rest.formDialogProps?.formProps?.defaultValues,
              },
            },
          }}
        />
        {openedForm && (
          <Dialog
            open={!!openedForm}
            onClose={() => setOpenedForm(null)}
            maxWidth="md"
          >
            <DialogTitle
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              Formulier
              <IconButton onClick={() => setOpenedForm(null)}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Overview form={openedForm} p={0} screen={false} />
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  },
);
